import React, { useEffect, useState } from "react";
import { FieldArray, Form, Formik, FormikProps } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  IconButton,
  InputLabel,
  Box,
  FormHelperText,
  Switch,
  FormControlLabel
} from "@mui/material";
import { styled } from '@mui/material/styles';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ManagersSelectInput from "../classifier/ManagersSelectInput";
import { useNotifications } from "../../hooks/NotificationProvider";
import AccessRuleModel, { AccessRuleForm, defaultAccessRuleForm, accessRuleSchema } from "../../core/api/accessRule/AccessRuleModel";
import { translations } from "../../translations/common";

const StyledTextField = styled(TextField)(() => ({
  margin: 0,
  width: '100%',
}));

const StyledButton = styled(Button)(() => ({
  margin: 0,
  width: '100%',
}));

const InputsContainer = styled(Box)({
  width: '100%',
  padding: 0,
  '& .MuiInputBase-root': { width: '100%' },
  '& .MuiOutlinedInput-root': { width: '100%' },
  '& .MuiFormControl-root': { width: '100%' }
});

const ArrayFieldList = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  margin: 0
}));

const StyledManagersSelect = styled(ManagersSelectInput)({
  width: '100%',
  '& .MuiFormControl-root': { width: '100%' }
});

interface Props {
  ruleId?: string | null;
  close(): void;
}

interface ApiError {
  field?: string;
  defaultMessage: string;
}

interface ArrayFieldProps {
  name: string;
  label: string;
  values: string[];
  errors: any;
  touched: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
}

const ArrayField: React.FC<ArrayFieldProps> = ({
  name,
  label,
  values,
  errors,
  touched,
  handleChange,
  handleBlur
}) => (
  <Grid size={{ xs: 12 }}>
    <InputLabel>{label} </InputLabel>
    <FieldArray
      name={name}
      render={arrayHelpers => (
        <ArrayFieldList>
          {values.map((value, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <StyledTextField
                name={`${name}[${index}]`}
                id={`${name}-${index}`}
                value={value}
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched[name] && errors[name] && errors[name][index])}
                helperText={touched[name] && errors[name] && errors[name][index]}
                size="small"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      color="secondary"
                      onClick={() => arrayHelpers.remove(index)}
                      size="small"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          ))}
          <StyledButton
            startIcon={<AddIcon />}
            onClick={() => arrayHelpers.push('')}
            variant="outlined"
            color="primary"
            size="small"
          >
            {translations.accessRules.form.buttons.add}
          </StyledButton>
          {touched[name] && errors[name] && typeof errors[name] === 'string' && (
            <FormHelperText error>{errors[name]}</FormHelperText>
          )}
        </ArrayFieldList>
      )}
    />
  </Grid>
);

const AccessRuleModalForm: React.FC<Props> = ({ ruleId, close }) => {
  const notifications = useNotifications();
  const [value, setValue] = useState<AccessRuleForm>(defaultAccessRuleForm);

  useEffect(() => {
    if (ruleId) {
      AccessRuleModel.findOne(ruleId).then(rule => setValue(rule));
    }
  }, [ruleId]);

  const handleSubmit = async (values: AccessRuleForm, actions: any) => {
    try {
      await AccessRuleModel.updateOrCreate({ ...values, id: ruleId || undefined });
      close();
      notifications.notify({
        level: "success",
        message: translations.accessRules.form.notifications.success.message
      });
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const fieldErrors = error.response.data.errors.reduce((acc: Record<string, string[]>, err: ApiError) => {
          const field = err.field || 'general';
          if (!acc[field]) acc[field] = [];
          acc[field].push(err.defaultMessage);
          return acc;
        }, {});
        actions.setErrors(fieldErrors);
      } else {
        notifications.notify({ level: "error", message: "An unexpected error occurred" });
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open
      onClose={close}
      aria-labelledby="form-dialog-title"
      PaperProps={{
        sx: {
          width: '520px',
          maxWidth: '100%',
          margin: 0
        }
      }}
    >
      <DialogTitle id="form-dialog-title">{translations.accessRules.form.title}</DialogTitle>
      <Formik
        initialValues={value}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={accessRuleSchema}
      >
        {(props: FormikProps<AccessRuleForm>) => {
          const { values, handleBlur, handleChange, isSubmitting, errors, touched } = props;
          return (
            <Form>
              <DialogContent>
                <InputsContainer>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }}>
                      <StyledManagersSelect
                        name="userId"
                        id="userId"
                        label={translations.accessRules.form.fields.userId}
                        value={values.userId}
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.userId && Boolean(errors.userId)}
                        helperText={touched.userId && errors.userId ? String(errors.userId) : undefined}
                        disabled={!!ruleId}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="enabled"
                            checked={values.enabled}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label={translations.accessRules.form.fields.enabled}
                      />
                    </Grid>
                    <ArrayField
                      name="ipAddresses"
                      label={translations.accessRules.form.fields.ipAddresses}
                      values={values.ipAddresses}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                    <ArrayField
                      name="browserIds"
                      label={translations.accessRules.form.fields.browserIds}
                      values={values.browserIds}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </Grid>
                </InputsContainer>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button type="button" onClick={close} disabled={isSubmitting}>
                  {translations.accessRules.form.buttons.cancel}
                </Button>
                <Button type="submit" disabled={isSubmitting} variant="contained" color="primary">
                  {translations.accessRules.form.buttons.save}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default AccessRuleModalForm;
